/**
 * PVP Battle API Client
 *
 * Frontend API client for PVP combat battles.
 * All battle logic executes server-side.
 */

import { API_BASE_URL } from '../config/apiConfig';
import { logClientError, logClientWarning } from './clientLogApi';
import { classifyClientIssue } from '../lib/clientIssueClassifier';
import { recordPlayerAction, markFirstBattleDone } from '../lib/playerEngagement';
import type { CombatAction, HandEvolutionEvent, TurnResult } from '@shared/types/pvpTypes';
import type { CardHandView } from '@shared/domain/cardTypes';
import type { PvpBattleMode, AiStrategy } from '@shared/domain/pvpConstants';
import type { BattleRewardDrop } from '@shared/domain/battleRewards';
import type { BattleEvolutionRank } from '@shared/domain/battleEvolution';

const API_VERSION = 'v1';
const API_BASE_PATH = '/api';
const JSON_HEADERS = { 'Content-Type': 'application/json' } as const;

function withBase(path: string): string {
  return `${API_BASE_URL}${API_BASE_PATH}/${API_VERSION}${path}`;
}

async function parseJson<T>(response: Response): Promise<T> {
  const data = await response.json();

  if (!response.ok) {
    const maybeObject = data as { error?: string } | null | undefined;
    const message = typeof maybeObject === 'object' ? maybeObject?.error : undefined;
    throw new Error(message || response.statusText || 'PVP API request failed');
  }

  return data as T;
}

/**
 * Serialized player state from API (subset of full PlayerState).
 */
export interface ApiPlayerState {
  hp: number;
  maxHp: number;
  level: number;
  magicCharge: boolean;
  recoverUses: number;
  maxRecoverUses: number;
  stunned: boolean;
  wounded: boolean;
  dotDamage: number;
  /**
   * Bleed ticks left on this fighter — the CLOCK the tick above is paid on.
   *
   * Two surfaces need it and neither could get it before: the status badge, which printed every
   * bleed as `×1` turn, and the Magic button, whose DETONATE punish is worth the tick times the
   * turns remaining (capped at `DETONATE_MAX_TICKS`). Optional because a battle serialised
   * before it was sent reads back without one.
   */
  dotTurnsRemaining?: number;
  shieldHp: number;
  pendingDrainHeal: number;
  /** Wound turns left — a wound on its last turn closes before the exchange reads it. */
  woundTurnsRemaining?: number;
  lastAction: CombatAction | null;
  sameActionStreak: number;
  /**
   * Suikamon cards this fighter has taken off the other THIS battle — the nameplate's
   * evolution pips. Optional so a battle serialised before the mechanic existed reads back
   * as a fighter holding none.
   */
  suikamonCards?: number;
  /** How far this fighter may climb (campaign nodes author it). Absent = the whole ladder. */
  maxEvolutionRank?: BattleEvolutionRank;
  /**
   * The maximum HP this fighter OPENED the battle on, before any evolution grew it.
   *
   * The HP bar draws the difference between this and `maxHp` as a marked band, so the length
   * an evolution added is a thing you can see rather than a bar that is quietly a new size.
   * Optional: a battle serialised before the field existed reads back as "nothing added".
   */
  evolutionBaseMaxHp?: number;
  /**
   * Rounds this fighter has already fought — the input to round pressure. The gesture buttons
   * price the NEXT round (`roundsFought + 1`) at the rate the engine will charge, so a button
   * cannot advertise damage the server would not deal once the late-fight ramp is running.
   */
  roundsFought?: number;
}

/**
 * Battle state from API.
 */
export interface ApiBattleState {
  battleId: string;
  turn: number;
  playerState: ApiPlayerState;
  opponentState: ApiPlayerState;
  turnHistory: TurnResult[];
  winner: 'player' | 'opponent' | 'draw' | null;
  /**
   * Battle Cards, present only in modes that deal them. This player's own hand plus public
   * counts — the opponent's cards are never sent, only how many they hold.
   */
  cards?: CardHandView;
}

/**
 * Response from create battle endpoint.
 */
export interface CreateBattleResponse {
  success: boolean;
  battleId: string;
  battle: ApiBattleState;
  error?: string;
  /** If blocked by active battle, the battle ID to resume */
  activeBattleId?: string;
  /** Ranked mode: info about the opponent picked by the backend */
  rankedMatch?: {
    opponent: {
      petId: string;
      name: string;
      speciesId: number;
      stage: number;
      level: number;
      eloRating: number;
      ownerAddress: string;
    };
  };
}

/**
 * Response from execute move endpoint.
 */
export interface ExecuteMoveResponse {
  success: boolean;
  turnResult: TurnResult;
  battle: ApiBattleState;
  winner: 'player' | 'opponent' | 'draw' | null;
  error?: string;
  /**
   * Battle Cards: what each side committed this turn — THE ONLY PLACE the opponent's cards
   * are ever sent, and only because the exchange has already resolved. It is the record of
   * a turn that happened, never a look at a hand the player could still play against.
   */
  turnCards?: {
    player: string[];
    opponent: string[];
    /**
     * Each side's move landed in a BOOSTED suit — its stack played one copy bigger.
     * A flag, not cards: a boost puts nothing on the board and costs nobody a draw.
     */
    playerBoosted: boolean;
    opponentBoosted: boolean;
  };
}

/** Response from the Battle Cards discard endpoint. */
export interface DiscardCardsResponse {
  success: boolean;
  cards?: CardHandView;
  /**
   * **A RUNG THAT LANDED ON THIS DEAL.** Settling the hand is what converts a held set of
   * Suikamon cards, so the evolution comes back on this call — the beat plays here, before the
   * move is chosen, rather than inside the round result. Absent whenever nothing converted.
   */
  evolution?: HandEvolutionEvent;
  error?: string;
}

/**
 * Response from get battle endpoint.
 */
export interface GetBattleResponse {
  success: boolean;
  battle: ApiBattleState;
  /** Player species ID (for battle restoration) */
  playerSpeciesId?: number;
  /** Opponent species ID (for battle restoration) */
  opponentSpeciesId?: number;
  /** Battle mode (for battle restoration) */
  mode?: PvpBattleMode;
  error?: string;
}

/**
 * Storage key for PVP session ID (localStorage)
 */
const SESSION_ID_STORAGE_KEY = 'suikamon_pvp_session_id';

/**
 * Storage keys for PVP battle routing (localStorage)
 * Used only for navigation between battle pages - NOT for state persistence.
 * State is always rebuilt from backend on component mount.
 * Using localStorage (not sessionStorage) to support cross-tab navigation.
 */
export const PVP_BATTLE_STATE_KEY = 'pvp_battle_state';
export const PVP_BATTLE_RESULT_KEY = 'pvp_battle_result';

/**
 * In-memory fallback session id, used when web storage is unavailable (e.g. the
 * Telegram in-app browser and some private-mode webviews expose `localStorage` as
 * `null` or throw on access). Keeps the session id stable for the page lifetime so
 * backend tracking / discount validation still work, even without persistence.
 */
let inMemorySessionId: string | null = null;

/** Generate a UUID v4 string. */
function generateSessionId(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Get or create a session ID for PVP battles.
 * Uses localStorage to persist anonymous session across page reloads.
 *
 * This session ID is used for:
 * - Backend API authentication and tracking
 * - Discount eligibility validation after challenge completion
 *
 * localStorage access is wrapped in try/catch: some environments (the Telegram
 * in-app browser, private-mode webviews) expose `localStorage` as `null` or throw
 * on access. This function is called during render (e.g. PvpChallengeMode, guest
 * identity), so an unguarded read would crash React with "Cannot read properties
 * of null (reading 'getItem')". When storage is unavailable we fall back to an
 * in-memory id — same guarded pattern as clearSessionId / guestPlay / sessionStore.
 */
export function getOrCreateSessionId(): string {
  let sessionId: string | null = null;
  try {
    sessionId = localStorage.getItem(SESSION_ID_STORAGE_KEY);
  } catch {
    // storage unavailable — fall through to the in-memory fallback below.
  }

  if (!sessionId) {
    sessionId = inMemorySessionId ?? generateSessionId();
    inMemorySessionId = sessionId;
    try {
      localStorage.setItem(SESSION_ID_STORAGE_KEY, sessionId);
    } catch {
      // private mode / quota / null storage — best-effort, keep in-memory id.
    }
  }

  return sessionId;
}

/**
 * Clear the current session ID from localStorage.
 * This forces creation of a new session ID on next call to getOrCreateSessionId().
 * Use this when resetting challenge progress to start a fresh backend session.
 */
export function clearSessionId(): void {
  inMemorySessionId = null;
  try {
    localStorage.removeItem(SESSION_ID_STORAGE_KEY);
  } catch (error) {
    console.error('Failed to clear PVP session ID:', error);
  }
}

/**
 * Storage keys for PVP battle routing in localStorage
 */
const PVP_LOCAL_STORAGE_KEYS = [
  PVP_BATTLE_STATE_KEY,
  PVP_BATTLE_RESULT_KEY,
] as const;

/**
 * Clear all PVP-related storage (localStorage).
 * Use this when wallet disconnects or changes to prevent session reuse.
 *
 * Clears:
 * - localStorage: suikamon_pvp_session_id, pvp_battle_state, pvp_battle_result
 *
 * Note: State is always rebuilt from backend, so no need to clear state cache.
 */
export function clearAllPvpStorage(): void {
  try {
    // Clear localStorage session ID
    localStorage.removeItem(SESSION_ID_STORAGE_KEY);

    // Clear all localStorage battle routing keys
    for (const key of PVP_LOCAL_STORAGE_KEYS) {
      localStorage.removeItem(key);
    }

    console.log('[PVP] Cleared all PVP storage due to wallet change');
  } catch (error) {
    console.error('Failed to clear PVP storage:', error);
  }
}

/**
 * Create a new PVP battle.
 *
 * @param playerSpeciesId - Player's species ID
 * @param opponentSpeciesId - Opponent's species ID
 * @param playerStage - Player's evolution stage (or level for admin test mode)
 * @param opponentStage - Opponent's evolution stage (or level for admin test mode)
 * @param aiStrategy - AI difficulty level (see AiStrategy type)
 * @param mode - Battle mode (use BATTLE_MODE constants from pvpConstants.ts)
 * @param seed - Optional RNG seed for reproducibility
 * @param walletAddress - Optional wallet address to link to session
 * @param petId - Optional pet ID for trial mode (required for trial battles)
 * @param playerPetId - Optional player pet ID for ranked mode (required for ranked battles)
 * @param opponentPetId - Optional opponent pet ID for ranked mode (required for ranked battles)
 * @param opponentWalletAddress - Optional opponent wallet address for ranked mode (required for ranked battles)
 * @param playerLevel - Optional player level (for accurate HP calculation)
 * @param opponentLevel - Optional opponent level (for accurate HP calculation)
 * @returns Battle ID and initial state
 */
export async function createBattle(
  playerSpeciesId: number,
  opponentSpeciesId: number,
  playerStage: number,
  opponentStage: number,
  aiStrategy: AiStrategy,
  mode: PvpBattleMode,
  seed?: number,
  walletAddress?: string,
  petId?: string,
  playerPetId?: string,
  opponentPetId?: string,
  opponentWalletAddress?: string,
  playerLevel?: number,
  opponentLevel?: number,
  excludeOpponentPetId?: string
): Promise<CreateBattleResponse> {
  const sessionId = getOrCreateSessionId();

  console.log('[PVP API] createBattle request', {
    sessionId: sessionId.slice(0, 8) + '...',
    playerSpeciesId,
    opponentSpeciesId,
    mode,
    playerLevel,
    opponentLevel,
  });

  try {
    const response = await fetch(withBase('/pvp/battles/create'), {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify({
        sessionId,
        playerSpeciesId,
        opponentSpeciesId,
        // For admin test mode, these are levels; for trial/ranked, these are stages
        playerStage: playerStage,
        opponentStage: opponentStage,
        // Send levels for all modes (for accurate HP calculation)
        playerLevel: playerLevel,
        opponentLevel: opponentLevel,
        aiStrategy,
        mode,
        seed,
        walletAddress,
        petId,
        // Ranked mode parameters
        playerPetId,
        opponentPetId,
        opponentWalletAddress,
        // Ranked rematch: hint backend to avoid re-matching the just-fought opponent
        excludeOpponentPetId,
      }),
    });

    // Special handling for 409 Conflict (active battle exists)
    // We need to preserve the activeBattleId from the response body
    const data = await response.json();

    console.log('[PVP API] createBattle response', {
      status: response.status,
      ok: response.ok,
      success: data.success,
      hasActiveBattleId: !!data.activeBattleId,
      activeBattleId: data.activeBattleId,
      error: data.error,
    });

    if (!response.ok) {
      // For 409 Conflict, return the response data (includes activeBattleId) — an
      // expected "already have an active battle" state, NOT a failure to report.
      if (response.status === 409) {
        console.log('[PVP API] 409 Conflict - active battle detected:', data.activeBattleId);
        return data as CreateBattleResponse;
      }

      // For other errors, throw (caught below for reporting, then rethrown to the caller).
      const message = data.error || response.statusText || 'PVP API request failed';
      throw new Error(message);
    }

    return data as CreateBattleResponse;
  } catch (error) {
    // Reaches here on a non-409 error status, a network drop, or an unparseable body —
    // the 409 path returns before throwing. Forward it so a battle-start failure is visible
    // server-side even when the request never landed a diagnosable backend log, but route it
    // through the shared classifier first: an EXPECTED gate (e.g. "Insufficient Suika balance
    // for ranked battle", which the UI already shows the player) must not page us or fill
    // error.log — only a genuine failure should. Rethrow unchanged so the caller's UI handling
    // is unaffected either way.
    const message = error instanceof Error ? error.message : 'PVP API request failed';
    const report = `[pvp-battle-create] ${message} (mode=${mode})`;
    const disposition = classifyClientIssue(message, 'createBattle');
    if (disposition === 'error') logClientError(report, 'createBattle');
    else if (disposition === 'warn') logClientWarning(report, 'createBattle');
    throw error;
  }
}

/**
 * Submit a move for the current turn.
 *
 * NO CARDS ARE SENT. A gesture plays every card of that gesture in the hand, so the meld is
 * a pure function of the action and the server's own copy of the hand — there is nothing for
 * a client to choose, send, or get wrong.
 *
 * @param battleId - Battle identifier
 * @param action - Player's chosen action
 * @returns Turn result, updated battle state, and both melds for the reveal
 */
export async function executeMove(
  battleId: string,
  action: CombatAction
): Promise<ExecuteMoveResponse> {
  const response = await fetch(withBase(`/pvp/battles/${battleId}/move`), {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify({ action }),
  });

  // Committing a gesture is the player DOING something, and this is the one call every
  // battle mode makes to do it — trial, ranked, challenge, squad and campaign alike. It is
  // counted so a player who adopts and goes straight into a fight, never touching the care
  // buttons, still earns their way past the optional popups. See `lib/playerEngagement`.
  recordPlayerAction();
  // …and it retires the pet screen's first-battle invitation for good.
  markFirstBattleDone();

  return parseJson<ExecuteMoveResponse>(response);
}

/**
 * Battle Cards, step one of the round: pitch cards and draw back up.
 *
 * The hand that comes back is FINAL — resolving the pitch is what unlocks the gesture
 * buttons, so this is the "pitch and play with these" half of the step. {@link keepHand} is
 * the other half.
 *
 * @param battleId - Battle identifier
 * @param cardIds - Cards to throw away, from the player's own hand
 * @returns The refilled hand, marked final for this round
 */
export async function discardCards(
  battleId: string,
  cardIds: readonly string[]
): Promise<DiscardCardsResponse> {
  const response = await fetch(withBase(`/pvp/battles/${battleId}/discard`), {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify({ cardIds }),
  });

  return parseJson<DiscardCardsResponse>(response);
}

/**
 * Battle Cards, step one of the round: accept the hand as dealt and give up the pitch.
 *
 * The other way out of the pitch step. Its own endpoint rather than a discard of nothing,
 * so a client bug that meant to pitch can never arrive looking like a deliberate keep.
 *
 * @param battleId - Battle identifier
 * @returns The same hand, marked final for this round
 */
export async function keepHand(battleId: string): Promise<DiscardCardsResponse> {
  const response = await fetch(withBase(`/pvp/battles/${battleId}/keep`), {
    method: 'POST',
    headers: JSON_HEADERS,
  });

  return parseJson<DiscardCardsResponse>(response);
}

/**
 * Get current battle state.
 *
 * @param battleId - Battle identifier
 * @returns Current battle state
 */
export async function getBattle(battleId: string): Promise<GetBattleResponse> {
  const response = await fetch(withBase(`/pvp/battles/${battleId}`), {
    method: 'GET',
  });

  return parseJson<GetBattleResponse>(response);
}

/**
 * Response from validate session endpoint.
 */
export interface ValidatePvpSessionResponse {
  /** Whether the session is eligible for discount */
  eligible: boolean;
  /** Discount percentage if eligible (value from pvpConstants) */
  discountPercent?: number;
  /** Eligible rarities if eligible (e.g., [0, 1] = Common, Rare) */
  eligibleRarities?: number[];
  /** Reason if not eligible */
  reason?: string;
  /** Whether wallet connection is required */
  requiresWalletConnection?: boolean;
}

/**
 * Response from get session endpoint.
 */
export interface GetPvpSessionResponse {
  sessionId: string;
  totalWins: number;
  totalLosses: number;
  totalBattles: number;
  hasMinted: boolean;
}

/**
 * Get PVP session details from the server.
 * Returns default values (zeros) if session not created yet.
 *
 * @param sessionId - Session ID to fetch
 * @returns Session data (real or default values)
 */
export async function getPvpSession(
  sessionId: string
): Promise<GetPvpSessionResponse> {
  const url = new URL(withBase('/pvp/session'));
  url.searchParams.set('sessionId', sessionId);

  const response = await fetch(url.toString(), {
    headers: {
      'Cache-Control': 'no-cache',
    },
  });

  return parseJson<GetPvpSessionResponse>(response);
}

/**
 * Battle history record for state reconstruction.
 */
export interface SessionBattleRecord {
  opponent_species_id: number;
  player_species_id: number;
  winner: 'player' | 'opponent' | 'draw';
}

/**
 * Response from get session battles endpoint.
 */
export interface GetSessionBattlesResponse {
  battles: SessionBattleRecord[];
  error?: string;
}

/**
 * Get battle history for a session (challenge mode only).
 * Used to reconstruct UI state after page reload.
 *
 * @param sessionId - Session ID to fetch battles for
 * @returns Array of battle records with species IDs and winner
 */
export async function getSessionBattles(
  sessionId: string
): Promise<SessionBattleRecord[]> {
  try {
    const response = await fetch(withBase(`/pvp/session/${sessionId}/battles`), {
      headers: {
        'Cache-Control': 'no-cache',
      },
    });

    if (!response.ok) {
      console.error('Failed to fetch session battles:', response.status);
      return [];
    }

    const data = await response.json() as GetSessionBattlesResponse;
    return data.battles || [];
  } catch (error) {
    console.error('Failed to fetch session battles:', error);
    return [];
  }
}

/**
 * Validate a PVP session for discount eligibility.
 * Checks if the session has completed required battles and has wallet linked.
 * Optionally links wallet to session if provided.
 *
 * @param sessionId - Session ID to validate
 * @param walletAddress - Optional wallet address to link to session
 * @returns Validation result with discount details
 */
export async function validatePvpSession(
  sessionId: string,
  walletAddress?: string
): Promise<ValidatePvpSessionResponse> {
  const url = new URL(withBase('/pvp/session/validate'));
  url.searchParams.set('sessionId', sessionId);
  if (walletAddress) {
    url.searchParams.set('walletAddress', walletAddress);
  }

  const response = await fetch(url.toString(), {
    headers: {
      'Cache-Control': 'no-cache',
    },
  });

  // Handle both 200 and 304 (Not Modified) as success
  if (response.ok || response.status === 304) {
    const data = await response.json();
    return data as ValidatePvpSessionResponse;
  }

  // For actual errors, return not eligible
  const data = await response.json().catch(() => ({}));
  return {
    eligible: false,
    reason: (data as { reason?: string })?.reason || 'Failed to validate session',
    requiresWalletConnection: false,
  };
}

/**
 * Pet trial battle stats.
 */
export interface PetTrialStats {
  totalBattles: number;
  wins: number;
  losses: number;
  draws: number;
  winRate: number;
}

/**
 * Response from get pet trial stats endpoint.
 */
export interface GetPetTrialStatsResponse {
  success: boolean;
  stats: PetTrialStats;
  error?: string;
}

/**
 * Get trial battle stats for a specific pet.
 *
 * @param petId - The pet's blockchain object ID
 * @returns Trial battle stats
 */
export async function getPetTrialStats(petId: string): Promise<PetTrialStats> {
  try {
    const response = await fetch(withBase(`/pvp/pet/${petId}/trial-stats`), {
      headers: {
        'Cache-Control': 'no-cache',
      },
    });

    const data = await parseJson<GetPetTrialStatsResponse>(response);
    return data.stats;
  } catch (error) {
    console.error('Failed to get pet trial stats:', error);
    return { totalBattles: 0, wins: 0, losses: 0, draws: 0, winRate: 0 };
  }
}

/**
 * Get ranked battle stats for a specific pet.
 *
 * @param petId - The pet's blockchain object ID
 * @returns Ranked battle stats
 */
export async function getPetRankedStats(petId: string): Promise<PetTrialStats> {
  try {
    const response = await fetch(withBase(`/pvp/pet/${petId}/ranked-stats`), {
      headers: {
        'Cache-Control': 'no-cache',
      },
    });

    const data = await parseJson<GetPetTrialStatsResponse>(response);
    return data.stats;
  } catch (error) {
    console.error('Failed to get pet ranked stats:', error);
    return { totalBattles: 0, wins: 0, losses: 0, draws: 0, winRate: 0 };
  }
}

/**
 * Response from complete trial battle endpoint.
 */
export interface CompleteTrialBattleResponse {
  /** Whether XP was successfully awarded */
  success: boolean;
  /** XP gained from this battle (0 if already awarded) */
  xpGained: number;
  /** Pet's new total XP */
  newXp: number;
  /** Suika cost for the battle */
  suikaCost?: number;
  /** Pet's new Suika balance after deduction */
  newSuikaBalance?: number;
  /**
   * **THE CHEST'S CONTENTS** — what the win rolled on top of its XP (bonus Suika,
   * sometimes Soul Shards), with the star rating and tap count the chest is drawn with.
   * Absent when the server granted no bonus (a loss, or an already-rewarded battle).
   */
  bonus?: BattleRewardDrop;
  /** Optional message (e.g., "XP already awarded") */
  message?: string;
  /** Error message if failed */
  error?: string;
}

/**
 * Award XP to player's pet after winning a trial battle.
 * Can be called multiple times safely (idempotent).
 *
 * @param battleId - The battle ID that was won
 * @param petId - The pet's blockchain object ID
 * @param walletAddress - The owner's wallet address
 * @returns XP reward result
 */
export async function completeTrialBattle(
  battleId: string,
  petId: string,
  walletAddress: string
): Promise<CompleteTrialBattleResponse> {
  try {
    const response = await fetch(withBase(`/pvp/battles/${battleId}/complete-trial`), {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify({
        petId,
        walletAddress,
      }),
    });

    return parseJson<CompleteTrialBattleResponse>(response);
  } catch (error) {
    console.error('Failed to complete trial battle:', error);
    return {
      success: false,
      xpGained: 0,
      newXp: 0,
      error: error instanceof Error ? error.message : 'Failed to award XP',
    };
  }
}

/**
 * Abandon a battle (player forfeits).
 * Marks the battle as complete with opponent as winner.
 * For ranked battles, automatically calculates and applies ELO/XP/Suika changes.
 *
 * @param battleId - The battle ID to abandon
 * @param sessionId - Optional session ID for challenge mode stats
 * @param playerPetId - Optional player pet ID for ranked battles
 * @param opponentPetId - Optional opponent pet ID for ranked battles
 * @param walletAddress - Optional wallet address for ranked battles
 * @returns Success status and ranked battle results (if applicable)
 */
export async function abandonBattle(
  battleId: string,
  sessionId?: string,
  playerPetId?: string,
  opponentPetId?: string,
  walletAddress?: string
): Promise<{
  success: boolean;
  error?: string;
  result?: {
    // Ranked battle fields
    oldElo?: number;
    newElo?: number;
    eloChange?: number;
    xpGained?: number;
    newXp?: number;
    // Common fields (both trial and ranked)
    suikaCost: number;
    newSuikaBalance: number;
  };
}> {
  try {
    const response = await fetch(withBase(`/pvp/battles/${battleId}/abandon`), {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify({ sessionId, playerPetId, opponentPetId, walletAddress }),
    });

    return parseJson<{
      success: boolean;
      error?: string;
      result?: {
        oldElo?: number;
        newElo?: number;
        eloChange?: number;
        xpGained?: number;
        newXp?: number;
        suikaCost: number;
        newSuikaBalance: number;
      };
    }>(response);
  } catch (error) {
    console.error('Failed to abandon battle:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to abandon battle',
    };
  }
}

// =====================================================
// Ranked Battle (ELO) API
// =====================================================

/**
 * Ranked opponent data from matchmaking endpoint.
 */
export interface RankedOpponent {
  pet_id: string;
  name: string;
  species_id: number;
  stage: number;
  level: number;
  elo_rating: number;
  owner_address: string;
}

/**
 * Response from ranked matchmaking endpoint.
 */
export interface FindRankedOpponentsResponse {
  success: boolean;
  playerElo: number;
  eloRange: number;
  opponents: RankedOpponent[];
  error?: string;
}

/**
 * Find ranked opponents for matchmaking.
 * Returns opponents within ELO range of player's pet.
 *
 * @param petId - Player's pet blockchain object ID
 * @param walletAddress - Player's wallet address
 * @returns Matchmaking results with player ELO and opponent list
 */
export async function findRankedOpponents(
  petId: string,
  walletAddress: string
): Promise<FindRankedOpponentsResponse> {
  try {
    const url = new URL(withBase('/pvp/ranked/matchmaking'));
    url.searchParams.set('petId', petId);
    url.searchParams.set('walletAddress', walletAddress);

    const response = await fetch(url.toString(), {
      headers: {
        'Cache-Control': 'no-cache',
      },
    });

    return parseJson<FindRankedOpponentsResponse>(response);
  } catch (error) {
    console.error('Failed to find ranked opponents:', error);
    return {
      success: false,
      playerElo: 1200,
      eloRange: 300,
      opponents: [],
      error: error instanceof Error ? error.message : 'Failed to find opponents',
    };
  }
}

/**
 * Milestone reward data returned when a tier threshold is crossed.
 */
export interface MilestoneReward {
  tier: string;
  emoji: string;
  eloThreshold: number;
  /** The Soul Shards the tier paid — 3-4 of a random Suikamon (Suika instead for a species you own). */
  shards: { speciesId: number; amount: number; suika?: number };
}

/**
 * Claimed milestone info for a pet.
 */
export interface ClaimedMilestone {
  tier: string;
  eloThreshold: number;
  breedingTokenGranted: boolean;
  claimedAt: string;
}

/**
 * Response from complete ranked battle endpoint.
 */
export interface CompleteRankedBattleResponse {
  /** Whether ELO was successfully updated */
  success: boolean;
  /** Player's old ELO rating */
  oldElo: number;
  /** Player's new ELO rating */
  newElo: number;
  /** ELO change (+/- value) */
  eloChange: number;
  /** XP gained from this battle (20 if won, 0 if lost) */
  xpGained: number;
  /** Pet's new total XP */
  newXp: number;
  /** Suika cost deducted for this battle */
  suikaCost: number;
  /** Pet's new Suika balance */
  newSuikaBalance: number;
  /** Milestone rewards granted during this battle (if any tier thresholds were crossed) */
  milestoneRewards?: MilestoneReward[];
  /**
   * **THE CHEST'S CONTENTS** — what the win rolled on top of its XP (bonus Suika,
   * sometimes Soul Shards), with the star rating and tap count the chest is drawn with.
   * Absent when the server granted no bonus (a loss, or an already-rewarded battle).
   */
  bonus?: BattleRewardDrop;
  /** Error message if failed */
  error?: string;
}

/**
 * Award ELO rating change after completing a ranked battle.
 * Can be called multiple times safely (idempotent).
 *
 * @param battleId - The battle ID that was completed
 * @param playerPetId - The player's pet blockchain object ID
 * @param opponentPetId - The opponent's pet blockchain object ID
 * @param walletAddress - The owner's wallet address
 * @returns ELO rating result
 */
export async function completeRankedBattle(
  battleId: string,
  playerPetId: string,
  opponentPetId: string,
  walletAddress: string
): Promise<CompleteRankedBattleResponse> {
  try {
    const response = await fetch(withBase(`/pvp/battles/${battleId}/complete-ranked`), {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify({
        playerPetId,
        opponentPetId,
        walletAddress,
      }),
    });

    return parseJson<CompleteRankedBattleResponse>(response);
  } catch (error) {
    console.error('Failed to complete ranked battle:', error);
    return {
      success: false,
      oldElo: 1200,
      newElo: 1200,
      eloChange: 0,
      xpGained: 0,
      newXp: 0,
      suikaCost: 0,
      newSuikaBalance: 0,
      error: error instanceof Error ? error.message : 'Failed to update ELO',
    };
  }
}

/**
 * Leaderboard entry.
 */
export interface EloLeaderboardEntry {
  pet_id: string;
  name: string;
  species_id: number;
  stage: number;
  level: number;
  elo_rating: number;
  owner_address: string;
}

/**
 * Response from ELO leaderboard endpoint.
 */
export interface GetEloLeaderboardResponse {
  success: boolean;
  leaderboard: EloLeaderboardEntry[];
  error?: string;
}

/**
 * Get ELO leaderboard (top pets by rating).
 *
 * @param limit - Number of entries to return (1-50, default 10)
 * @returns Leaderboard entries
 */
export async function getEloLeaderboard(
  limit: number = 10
): Promise<EloLeaderboardEntry[]> {
  try {
    const url = new URL(withBase('/pvp/ranked/leaderboard'));
    url.searchParams.set('limit', Math.min(Math.max(limit, 1), 50).toString());

    const response = await fetch(url.toString(), {
      headers: {
        'Cache-Control': 'no-cache',
      },
    });

    const data = await parseJson<GetEloLeaderboardResponse>(response);
    return data.leaderboard || [];
  } catch (error) {
    console.error('Failed to get ELO leaderboard:', error);
    return [];
  }
}

/**
 * Response from the ranked milestones endpoint.
 * `newlyGrantedMilestones` contains any tier the backend just self-healed
 * during this request (pet qualified by ELO but hadn't been credited yet).
 */
export interface PetMilestonesResponse {
  claimedMilestones: ClaimedMilestone[];
  newlyGrantedMilestones: MilestoneReward[];
}

/**
 * Get claimed ranked milestones for a pet.
 * Used by ranked battle UI to show tier progress and "next reward" indicator.
 *
 * The backend reconciles unclaimed tiers against the pet's current ELO on
 * every call, so a stuck pet recovers as soon as the player opens the page.
 *
 * @param petId - Pet blockchain object ID
 * @returns Claimed milestones plus any newly granted ones
 */
export async function fetchPetMilestones(
  petId: string
): Promise<PetMilestonesResponse> {
  try {
    const response = await fetch(withBase(`/pvp/ranked/milestones/${petId}`), {
      headers: { 'Cache-Control': 'no-cache' },
    });

    const data = await parseJson<{
      success: boolean;
      claimedMilestones: ClaimedMilestone[];
      newlyGrantedMilestones?: MilestoneReward[];
      error?: string;
    }>(response);
    return {
      claimedMilestones: data.claimedMilestones || [],
      newlyGrantedMilestones: data.newlyGrantedMilestones || [],
    };
  } catch (error) {
    console.error('Failed to fetch pet milestones:', error);
    return { claimedMilestones: [], newlyGrantedMilestones: [] };
  }
}
